import React from 'react';
import { StatusBadge } from '../../ui';
import SectionCard, { SectionEmpty } from './SectionCard';
import { BADGE_STATUS, RUN_OUTCOME, fmtCount, fmtDateTime, fmtDuration, fmtPct, fmtRelative, runPath } from './format';

function ProgressBar({ totals }) {
  const total = Math.max(totals.total || 0, totals.passed + totals.failed + totals.skipped + (totals.pending || 0));
  if (!total) return null;
  const pct = (n) => `${(n / total) * 100}%`;
  return (
    <div
      className="pqd-progress"
      role="img"
      aria-label={`${totals.passed} passed, ${totals.failed} failed, ${totals.skipped} skipped, ${totals.pending || 0} pending of ${total}`}
    >
      <span className="pqd-progress__seg pqd-progress__seg--passed" style={{ width: pct(totals.passed) }} />
      <span className="pqd-progress__seg pqd-progress__seg--failed" style={{ width: pct(totals.failed) }} />
      <span className="pqd-progress__seg pqd-progress__seg--skipped" style={{ width: pct(totals.skipped) }} />
    </div>
  );
}

export default function LatestExecution({ section, navigateTo }) {
  const { data, loading, error, retry, refreshing } = section;
  const run = data?.run;
  const outcome = RUN_OUTCOME[run?.outcome] || RUN_OUTCOME.unknown;
  const running = run?.state === 'running';

  return (
    <SectionCard
      id="pqd-latest"
      title="Latest execution"
      subtitle={running ? `Live — refreshing every ${data.poll_interval_seconds}s` : 'Most recent test plan or standalone suite run'}
      loading={loading}
      error={error}
      onRetry={retry}
      refreshing={refreshing}
      actions={
        <button type="button" className="rt2-link" onClick={() => navigateTo('executions')}>
          All executions
        </button>
      }
    >
      {!run ? (
        <SectionEmpty
          title="No executions yet"
          action={
            <button type="button" className="rt-btn rt-btn--primary rt-btn--sm" onClick={() => navigateTo('test-plans')}>
              Run a test plan
            </button>
          }
        >
          Run a test plan or suite to see live results here.
        </SectionEmpty>
      ) : (
        <div className="pqd-latest">
          <div className="pqd-latest__head">
            <div>
              <div className="pqd-latest__name">
                {run.name}
                <span className="pqd-chip">{run.type === 'plan' ? 'Test plan' : 'Suite'} · #{run.id}</span>
              </div>
              <div className="pqd-latest__meta">
                Started {fmtDateTime(run.started_at)} ({fmtRelative(run.started_at)})
                {run.environment?.name && <> · {run.environment.name}</>}
                {run.executed_by?.name && <> · by {run.executed_by.name}</>}
              </div>
            </div>
            <StatusBadge status={BADGE_STATUS[outcome.tone]}>
              {running && <span className="pqd-live-dot" aria-hidden="true" />}
              {outcome.label}
            </StatusBadge>
          </div>

          <dl className="pqd-latest__stats">
            <div><dt>Total</dt><dd>{fmtCount(run.totals.total)}</dd></div>
            <div><dt>Passed</dt><dd className="is-success">{fmtCount(run.totals.passed)}</dd></div>
            <div><dt>Failed</dt><dd className="is-danger">{fmtCount(run.totals.failed)}</dd></div>
            <div><dt>Skipped</dt><dd>{fmtCount(run.totals.skipped)}</dd></div>
            {running ? (
              <div><dt>Pending</dt><dd>{fmtCount(run.totals.pending)}</dd></div>
            ) : (
              <div><dt>Pass rate</dt><dd>{fmtPct(run.pass_rate)}</dd></div>
            )}
            <div><dt>Duration</dt><dd>{running ? 'In progress' : fmtDuration(run.duration)}</dd></div>
          </dl>
          <ProgressBar totals={run.totals} />
          {run.raw_status && outcome.label.toLowerCase() !== String(run.raw_status).toLowerCase() && (
            <p className="pqd-footnote">Recorded status: {run.raw_status}</p>
          )}

          {data.failed_tests?.length > 0 && (
            <div className="pqd-latest__failed">
              <h3>Failed tests{data.failed_tests_total > data.failed_tests.length ? ` (showing ${data.failed_tests.length} of ${data.failed_tests_total})` : ''}</h3>
              <ul>
                {data.failed_tests.map((t) => (
                  <li key={t.execution_id}>
                    <button
                      type="button"
                      className="rt2-link"
                      onClick={() =>
                        navigateTo(
                          `executions/${t.execution_id}?${new URLSearchParams({ type: 'case', entity_id: String(t.test_case_id), name: t.name || '' })}`
                        )
                      }
                    >
                      {t.name || `Test case #${t.test_case_id}`}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="pqd-latest__actions">
            <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={() => navigateTo(runPath(run))}>
              Open run report
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
